import type { EvaluatorDecision, EvaluatorRubricData, RubricCriterionKey } from '@/api/evaluator-portal';

export interface RubricCriterionDefinition {
  key: RubricCriterionKey;
  label: string;
  shortLabel: string;
  description: string;
  levels: Record<1 | 2 | 3 | 4 | 5, string>;
}

export type RubricSelectionState = Record<RubricCriterionKey, number | null>;

export const EVALUATOR_RUBRIC_SCORES = [5, 4, 3, 2, 1] as const;

export const EVALUATOR_RUBRIC_LEVEL_LABELS: Record<1 | 2 | 3 | 4 | 5, string> = {
  5: 'Excelente',
  4: 'Bueno',
  3: 'Suficiente',
  2: 'Deficiente',
  1: 'Muy deficiente',
};

export const EVALUATOR_RUBRIC_CRITERIA: RubricCriterionDefinition[] = [
  {
    key: 'calidad',
    label: 'Calidad',
    shortLabel: 'Calidad',
    description: 'Congruencia entre objetivos, metas, fundamentación teórica y metodológica, justificación y alcance de resultados.',
    levels: {
      5: 'Existe total coherencia entre todos los elementos; metodología sólida y claramente alineada con objetivos; justificación robusta; resultados esperados bien definidos y alcanzables.',
      4: 'Alta coherencia general; ligeras áreas de mejora en la alineación metodológica o en la claridad de resultados.',
      3: 'Coherencia parcial; algunos elementos no están completamente articulados; metodología o resultados poco claros.',
      2: 'Inconsistencias notorias entre objetivos, metodología y resultados; justificación débil.',
      1: 'Falta de congruencia general; elementos desarticulados o ausentes.',
    },
  },
  {
    key: 'contribucion',
    label: 'Contribución',
    shortLabel: 'Contribución',
    description: 'Impacto potencial (económico, social, ambiental, político, tecnológico, científico o académico) y resultados de la investigación.',
    levels: {
      5: 'Impacto claramente identificado, relevante y multidimensional; resultados con alto potencial de aplicación o generación de conocimiento.',
      4: 'Impacto bien definido en al menos un ámbito; resultados pertinentes.',
      3: 'Impacto identificado pero limitado o poco desarrollado; resultados modestos.',
      2: 'Impacto poco claro o débil; resultados con baja relevancia.',
      1: 'No se identifica impacto ni contribución significativa.',
    },
  },
  {
    key: 'relevancia',
    label: 'Relevancia',
    shortLabel: 'Relevancia',
    description: 'Importancia del tema y diferenciación respecto a otros trabajos similares.',
    levels: {
      5: 'Tema altamente relevante, innovador y claramente diferenciado.',
      4: 'Tema relevante con algunos elementos diferenciadores.',
      3: 'Tema común con escasa diferenciación.',
      2: 'Baja relevancia; escasa aportación frente a otros trabajos.',
      1: 'Tema irrelevante o redundante.',
    },
  },
  {
    key: 'pertinencia',
    label: 'Pertinencia',
    shortLabel: 'Pertinencia',
    description: 'Actualidad y oportunidad del tema; capacidad de generar aportes significativos.',
    levels: {
      5: 'Tema completamente actual y pertinente; responde a problemáticas vigentes con alto potencial de aporte.',
      4: 'Tema actual y pertinente, con aportes claros.',
      3: 'Tema relativamente actual; aportes limitados.',
      2: 'Tema poco actual o con escasa aplicabilidad.',
      1: 'Tema desactualizado o irrelevante en el contexto actual.',
    },
  },
  {
    key: 'atencionUsuariosBeneficiarios',
    label: 'Atención de usuarios/beneficiarios',
    shortLabel: 'Atención de usuarios/beneficiarios',
    description: 'Identificación y consideración de actores beneficiados o impactados.',
    levels: {
      5: 'Identifica claramente múltiples beneficiarios; describe cómo serán impactados directa y significativamente.',
      4: 'Identifica beneficiarios claros con impacto definido.',
      3: 'Mención general de beneficiarios sin profundidad.',
      2: 'Beneficiarios poco claros o débilmente vinculados.',
      1: 'No se identifican beneficiarios.',
    },
  },
  {
    key: 'estiloPresentacion',
    label: 'Estilo y presentación',
    shortLabel: 'Estilo y presentación',
    description: 'Claridad, redacción, ortografía y cumplimiento de estructura.',
    levels: {
      5: 'Redacción clara, precisa y fluida; sin errores; estructura completamente adecuada.',
      4: 'Redacción clara con mínimos errores; estructura adecuada.',
      3: 'Redacción comprensible con varios errores menores; estructura parcialmente adecuada.',
      2: 'Redacción confusa; errores frecuentes; estructura deficiente.',
      1: 'Redacción deficiente; numerosos errores; no cumple con la estructura.',
    },
  },
];

export const EMPTY_RUBRIC_DATA: RubricSelectionState = {
  calidad: null,
  contribucion: null,
  relevancia: null,
  pertinencia: null,
  atencionUsuariosBeneficiarios: null,
  estiloPresentacion: null,
};

export function mergeRubricData(input?: EvaluatorRubricData | null): RubricSelectionState {
  return {
    calidad: input?.calidad ?? null,
    contribucion: input?.contribucion ?? null,
    relevancia: input?.relevancia ?? null,
    pertinencia: input?.pertinencia ?? null,
    atencionUsuariosBeneficiarios: input?.atencionUsuariosBeneficiarios ?? null,
    estiloPresentacion: input?.estiloPresentacion ?? null,
  };
}

export function countCompletedCriteria(rubricData: EvaluatorRubricData | null | undefined): number {
  return EVALUATOR_RUBRIC_CRITERIA.reduce((count, criterion) => {
    const value = rubricData?.[criterion.key];
    return Number.isInteger(value) ? count + 1 : count;
  }, 0);
}

export function hasCompleteRubric(rubricData: EvaluatorRubricData | null | undefined): boolean {
  return countCompletedCriteria(rubricData) === EVALUATOR_RUBRIC_CRITERIA.length;
}

export function computeRubricTotal(rubricData: EvaluatorRubricData | null | undefined): number {
  return EVALUATOR_RUBRIC_CRITERIA.reduce((total, criterion) => {
    const value = rubricData?.[criterion.key];
    return total + (Number.isInteger(value) ? Number(value) : 0);
  }, 0);
}

export function deriveVisualDecision(totalScore: number, isComplete: boolean): EvaluatorDecision | null {
  if (!isComplete) return null;
  if (totalScore >= 18) return 'APPROVED';
  if (totalScore >= 12) return 'OBSERVATIONS';
  return 'REJECTED';
}
